// HER CONTINUITY LAW, CLIENT HALF — **WHAT THE BRIDGE HANDS THE SIGNUP FORM.**
//
// Her clause: signup from the funnel's bridge arrives **PRE-FILLED with everything they gave**; they
// only finish and go; their chart data lands in their new room without re-entry.
//
// The SERVER half is `server/funnel-handoff.js`: the token names a ROW, the server reads the row, and
// `prefillFromLead` decides what may travel. This is the other end — **what those fields become on
// the front door's own form** — and it is a module of its own for the reason every law here is:
// inside App.tsx it could only ever be checked by a regex, and out here `scripts/funnel-capture.mjs`
// RUNS it over every minute of the day.
//
// It imports NOTHING but a type, which is `src/lib/*.ts`'s entry requirement and the property that
// lets a `.mjs` gate load it.
//
// ================================================================================================
// **THE CLOCKS DISAGREE, AND THAT IS THE WHOLE ROUND.**
//
// The funnel's time field is `<input type="time">`, which is a **24-hour** value (`"00:30"`,
// `"13:45"`). The house's front door is a **12-HOUR** field beside an AM/PM select
// (`BirthChartForm.birthTime` + `birthPeriod`), and `convertClientBirthTime` on the server reads it
// that way. Carrying the funnel's string across unchanged does not fail — it CASTS:
//
//   · `"13:45"` lands as 1:45 **AM**, a chart twelve hours out, and nothing anywhere is red.
//   · `"00:30"` lands as hour 0, and the server's own guard is `if (!rawHour)` — **zero is falsy** —
//     so midnight resolves to `null` and the whole payload is refused. A visitor born just after
//     midnight gets no reading at all, silently.
//
// Both are the same defect this house has paid for before (`personToChart`'s note: a missing
// `birthPeriod` "renders a select with nothing selected and casts a chart twelve hours out"). So the
// conversion is a NAMED law with the two boundary hours as its own cases, and the fixture holds it
// as a **BICONDITIONAL against `convertToTwentyFourHour`** — the house's existing 12-to-24 reader,
// which is the only honest answer key there is: what this function writes, that function must read
// back as the exact minute the visitor typed, for all 1,440 of them.
// ================================================================================================
import type { BirthChartForm } from "../components/BirthFields";
// The shared funnel law. `src/lib/*.ts` may read one — `avatar-photo.ts`, `pwa.ts` and
// `support-read.ts` already do — because the property that matters is that a `.mjs` gate can still
// load the whole chain, and `src/funnelCapture.js` imports nothing but `./funnels.js`.
import { funnelClock } from "../funnelCapture.js";

/** What the prefill door hands back. Kept as its own shape rather than reusing `BirthChartForm`,
 *  because the funnel asks for FIVE things and the front door has twenty fields — and a type that
 *  claimed the rest would invite a later round to fill them in from a lead that never had them. */
export type FunnelPrefill = {
  email?: string;
  birthDate?: string;
  /** 24-hour `HH:MM`, or "" when she gave none. The empty string is the TIMELESS LAW's own carrier. */
  birthTime?: string;
  birthPlace?: string;
  latitude?: string;
  longitude?: string;
  timezone?: string;
  funnel?: string;
};

/**
 * **A 24-HOUR CLOCK AS THE FRONT DOOR'S 12-HOUR ONE — AND THE DECLARATION MOVED (2026-08-13).**
 *
 * The law now lives in `src/funnelCapture.js`, the shared `.js` both this module and the SERVER read,
 * because the server composes the CAST from the same string this composes the pre-fill from. It was
 * declared here, in a client-only module the server cannot import — so the cast path had no reader at
 * all and did its own thing, which is precisely how her own submission came to be refused (R28/R29:
 * two answers to *what o'clock is this*, and one of them casts twelve hours out).
 *
 * **IT IS RE-EXPORTED UNDER ITS ORIGINAL NAME RATHER THAN DELETED**, because callers and instruments
 * name it — one declaration, one alias, and no second implementation anywhere (R165: what is gone is
 * the BODY, not the name a reader looks for).
 */
export { funnelClock as twelveHourFromTwentyFour };

/**
 * **THE PATCH THE FRONT DOOR RECEIVES — AND IT TRAVELS WHOLE (R118).**
 *
 * R118's ruling, paid one form over: *a patch is MERGED, so an omitted key is not "no value" — it is
 * the LAST value*. On this screen the form starts empty, so the merge is harmless today and would
 * stop being harmless the first time a visitor came back through a second funnel — so every key this
 * law is about is written on EVERY answer, including the empty ones.
 *
 * **`birthTimeUnknown` IS SET RATHER THAN LEFT ALONE, AND THAT IS THE TIMELESS LAW CROSSING THE
 * BRIDGE.** A visitor who left the time blank on the funnel told this house something, and her free
 * reading was cast on it (`leadIsTimeless`). Landing on a front door with the box unticked would ask
 * her the one question she already answered — and the first thing she would do is guess, which is
 * exactly what the timeless law exists to stop.
 *
 * **THE NAME IS NOT HERE AT ALL.** The funnel never asked for one, and the front door requires a
 * first, middle and last (T2-2's middle-name law). A pre-filled name would be an invention on the
 * one screen this house asks a stranger to trust — so those fields stay hers to type, which is also
 * what makes the form's own validation still mean something.
 */
export function prefillPatch(prefill: FunnelPrefill | null | undefined): Partial<BirthChartForm> {
  if (!prefill) return {};
  const clock = funnelClock(String(prefill.birthTime || ""));
  return {
    birthDate: String(prefill.birthDate || "").slice(0, 10),
    birthTime: clock ? clock.birthTime : "",
    birthPeriod: clock ? clock.birthPeriod : "AM",
    birthTimeUnknown: !clock,
    birthplace: String(prefill.birthPlace || ""),
    latitude: String(prefill.latitude || ""),
    longitude: String(prefill.longitude || ""),
    timezone: String(prefill.timezone || ""),
  };
}

/** Did the handoff actually carry anything? A token that resolved to a row with nothing usable in it
 *  is not a pre-fill, and telling her *"we kept your details"* over an empty form is worse than
 *  saying nothing (R18: a read that arrived empty and a read that never happened are two facts, and
 *  only this one is safe to announce). */
export const prefillHasSomething = (prefill: FunnelPrefill | null | undefined): boolean =>
  Boolean(String(prefill?.birthDate || "").trim());

/** The one sentence the front door says when it arrives carrying her details, so the continuity is
 *  visible rather than merely true — she gave these to a landing page minutes ago and needs to know
 *  why they are already here. */
export const PREFILL_NOTE = "I kept the details you gave me. Finish your name and you are in.";

/**
 * **L12 — WHAT THE SIGN-IN DOOR'S ADDRESS FIELD HOLDS, AND IT HAS TO SURVIVE ARRIVING LATE.**
 *
 * L10 gave `SignInModal` a `leadEmail` prop and the door read it with
 * `useState(member?.email || leadEmail || "")` — **evaluated once, at mount.** Measured at
 * coordinates: the bridge opens that door at **276ms** and the prefill fetch resolves after it, so
 * the address never reached the field. **It renders perfectly**: a stranger off an advertisement is
 * asked for the address she typed ninety seconds ago, on a field that is `required`, which is L10's
 * own continuity clause unmet at the one door it sends her through.
 *
 * **THREE INPUTS AND THE ORDER OF THEM IS THE WHOLE LAW (R18).**
 *
 *   · **WHAT IS IN THE BOX WINS, ALWAYS.** This runs when the lead's details ARRIVE, which can be
 *     mid-keystroke — and a field that rewrites itself under somebody's fingers is worse than one
 *     that was never filled.
 *   · **THEN THE MEMBER'S OWN ADDRESS**, which keeps L10's ruling intact word for word: a member
 *     signing in on a shared phone must never meet a stranger's address in the field.
 *   · **THEN THE LEAD'S.**
 *
 * It is a pure law rather than three lines inside the component **because a law inside a `.tsx` file
 * that renders `ModalBackdrop` cannot be RUN by any gate in this repo** (R8/R95) — and the wiring is
 * held separately, by position, because a perfect law nothing calls is not a law (R28/R52/R138/R165).
 */
export function signInEmailSeed(
  current: string | null | undefined,
  { memberEmail = "", leadEmail = "" }: { memberEmail?: string | null; leadEmail?: string | null } = {},
): string {
  if (String(current ?? "").trim()) return String(current);
  if (String(memberEmail ?? "").trim()) return String(memberEmail);
  return String(leadEmail ?? "").trim() ? String(leadEmail) : "";
}

/**
 * **THE BRIDGE ASKS FOR TWO DIFFERENT THINGS AND THEY ARE TWO FACTS (R18).**
 *
 * `lead` is *whose details these are*; `join` is *open the door she just tapped*. They are not one
 * flag with two spellings, and the proof is in the repo: `server/funnel-email.js` composes
 * `?join=1` with **NO token at all**, because the mailed bridge deliberately does not carry a
 * ninety-minute key to somebody's birth date through an inbox. So the join half must work standing
 * alone, and a reader that treated the token as the trigger would leave every mailed bridge dead.
 */
export const FUNNEL_LEAD_PARAM = "lead";
export const FUNNEL_JOIN_PARAM = "join";

/**
 * **DID SHE ASK FOR THE SIGNUP DOOR?**
 *
 * **THE FALSE SPELLINGS ARE NAMED RATHER THAN LEFT TO JAVASCRIPT (R136/R137).** A query value is
 * always a string, and `Boolean("false")` and `Boolean("0")` are both `true` — the exact coercion
 * that has cost this house a whole chart cast at the equator. A bare truthiness test would open the
 * door on `?join=0`, which is a stranger meeting a sign-in box she explicitly said no to.
 *
 * Both composers in this repo write `1`; the affirmative spellings are accepted anyway, because the
 * cost of being wrong that way is a hand-written link that works, and the cost of the other is a
 * hand-written link that silently does nothing (R19: the recoverable direction).
 */
const JOIN_REFUSED = ["", "0", "false", "no", "off"];
export function joinRequested(search: string): boolean {
  const raw = new URLSearchParams(String(search || "")).get(FUNNEL_JOIN_PARAM);
  if (raw === null) return false;
  return !JOIN_REFUSED.includes(String(raw).trim().toLowerCase());
}

/**
 * **THE HANDOFF IS READ OFF THE ADDRESS AND THEN THE ADDRESS IS CLEANED.**
 *
 * The bridge lands on `/?join=1&lead=<token>`. Left in place that token sits in her browser history,
 * in anything she shares and in every referrer this site sends afterwards — a standing key to a row
 * carrying somebody's birth date (its ninety-minute expiry is what bounds the damage, not what
 * prevents it). It is read once and swept, which is `HouseInbox`'s own stamp discipline one door up.
 *
 * **IT TAKES BOTH PARAMS, AND THAT SUPERSEDES `addressWithoutLead`, WHICH IS DELETED AT ITS
 * DECLARATION RATHER THAN LEFT STANDING BESIDE THIS ONE** (DL-2/DT-2b/R181). The old law returned
 * `""` when there was no token, so on a mailed bridge — `?join=1`, no token — nothing was swept at
 * all and a refresh re-opened the door she had just closed.
 */
export function addressWithoutFunnelHandoff(search: string, pathname = "/"): string {
  const params = new URLSearchParams(String(search || ""));
  if (!params.has(FUNNEL_LEAD_PARAM) && !params.has(FUNNEL_JOIN_PARAM)) return "";
  params.delete(FUNNEL_LEAD_PARAM);
  params.delete(FUNNEL_JOIN_PARAM);
  const rest = params.toString();
  return `${pathname}${rest ? `?${rest}` : ""}`;
}

/**
 * ================================================================================================
 * **THE PRE-FILL HAS TO SURVIVE A FULL PAGE NAVIGATION, AND THAT IS THE HALF L10 REALLY TURNS ON.**
 *
 * The signup surface is behind a session (`showOnboarding`'s very first clause is `isLoggedIn`), and
 * the way in that this house tells every member to use is **"Continue with Google"** — which is
 * `window.location.href = "/api/auth/google/start"`, a real navigation, returning to
 * `/?auth=google-success`. React remounts, `birthChart` is a fresh empty form, and the token was
 * already swept off the address on the way in.
 *
 * **So her details died at precisely the door the bridge sent her through.** Measured at coordinates
 * before this existed: a stranger landing on `/?join=1&lead=<token>` signed out saw a home page
 * byte-identical to `/`, and the one thing she could do next threw her details away.
 *
 * The carrier is `sessionStorage`, for `onboarding.ts`'s own recorded reason one module over: it
 * survives a refresh and a whole round trip to Google, and a new session starts clean — a
 * `localStorage` stash would pour a stranger's birth date over the form of whoever uses this browser
 * next week.
 *
 * **WHAT IS STASHED IS THE SERVER'S ANSWER, NEVER THE PATCH.** `prefillPatch` stays the ONE law that
 * turns a lead into form fields; stashing its output would give the 24-hour clock conversion a second
 * carrier, and two answers to *what o'clock is this* is the defect that shipped this whole redesign
 * (R28/R29).
 *
 * `store` is a PARAMETER so a gate RUNS this (R46/R63's shape, `readDismissed`'s exactly). A storage
 * that throws — Safari in private mode — reads as **nothing stashed**, which is the safe direction:
 * the worst case is a form she fills in herself, never a screen she cannot reach.
 * ================================================================================================
 */
export const FUNNEL_PREFILL_KEY = "twelve_house_funnel_prefill";

export function writeStashedPrefill(
  store: Pick<Storage, "setItem" | "removeItem"> | null | undefined,
  prefill: FunnelPrefill | null | undefined,
): void {
  try {
    // **A STASH IS ONLY WRITTEN FOR SOMETHING WORTH POURING.** The live door already refuses to
    // announce a pre-fill that carried nothing (R18: a read that arrived empty and a read that never
    // happened are two facts); the same test decides what may be kept, so the two cannot disagree.
    if (!prefillHasSomething(prefill)) { store?.removeItem(FUNNEL_PREFILL_KEY); return; }
    store?.setItem(FUNNEL_PREFILL_KEY, JSON.stringify(prefill));
  } catch {
    /* a storage that refuses is not a reason to lose the journey */
  }
}

export function readStashedPrefill(store: Pick<Storage, "getItem"> | null | undefined): FunnelPrefill | null {
  try {
    const raw = store?.getItem(FUNNEL_PREFILL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(String(raw));
    // Corrupt, empty or somebody else's shape reads as nothing rather than being poured over her
    // form — R39/R47's ruling on the live door, applied to the stash that stands in for it.
    return prefillHasSomething(parsed) ? (parsed as FunnelPrefill) : null;
  } catch {
    return null;
  }
}

export function clearStashedPrefill(store: Pick<Storage, "removeItem"> | null | undefined): void {
  try {
    store?.removeItem(FUNNEL_PREFILL_KEY);
  } catch {
    /* nothing to do; it dies with the tab either way */
  }
}
